import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditCategoryMutation } from "@modules/categorys/services/mutations";
import { CategoryFormValues, categorySchema } from "@modules/categorys/validations";
import { listCategorysQueryKey } from "@modules/categorys/services/queries/listCategorys/query";

export function useEditCategoryModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditCategoryMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listCategorysQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("category_admin.success.edit"),
      });
      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: CategoryFormValues) => {
    const categoryId = modalRef.current?.getId();
    await mutateAsync({ id: categoryId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
